import { createClient, RawQueryResponse } from 'next-sanity'

import {apiVersion, dataset, projectId, STUDIO_ORIGIN, token, useCdn} from '../env'

export type QueryParams = {[key: string]: string | number | boolean | string[]}

const DEFAULT_PARAMS = {} as QueryParams
const DEFAULT_TAGS = [] as string[]

export const client = createClient({
  apiVersion,
  dataset,
  projectId,
  useCdn,
  stega: {
    enabled: true,
    studioUrl: STUDIO_ORIGIN,
  },
})

export const prodClient = createClient({
  apiVersion,
  dataset,
  projectId,
  useCdn,
  stega: {
    enabled: false,
    studioUrl: STUDIO_ORIGIN,
  },
})

export async function sanityFetch<QueryResponse>({
                                                   query,
                                                   params = DEFAULT_PARAMS,
                                                   tags = DEFAULT_TAGS,
                                                   isDraftMode = false,
                                                 }: {
  query: string
  params?: QueryParams
  tags?: string[],
  isDraftMode?: boolean,
}): Promise<RawQueryResponse<QueryResponse>> {

  if (isDraftMode && !token) {
    throw new Error('The `SANITY_API_READ_TOKEN` environment variable is required.')
  }

  return (isDraftMode ? client : prodClient).fetch<QueryResponse>(query, params, {
    ...(isDraftMode && {
      token: token,
      perspective: 'previewDrafts',
    }),
    next: {
      revalidate: isDraftMode ? 0 : 20,
      tags,
    },
    filterResponse: false
  })
}

// Separate tagged fetch function for revalidation
export async function sanityFetchWithTags<QueryResponse>({
                                                   query,
                                                   params = DEFAULT_PARAMS,
                                                   tags = DEFAULT_TAGS,
                                                   isDraftMode = false,
                                                 }: {
  query: string
  params?: QueryParams
  tags?: string[],
  isDraftMode?: boolean,
}): Promise<RawQueryResponse<QueryResponse>> {

  if (isDraftMode && !token) {
    throw new Error('The `SANITY_API_READ_TOKEN` environment variable is required.')
  }

  // Always include 'sanity' tag for general cache invalidation
  const allTags = ['sanity', ...tags];

  return (isDraftMode ? client : prodClient).fetch<QueryResponse>(query, params, {
    ...(isDraftMode && {
      token: token,
      perspective: 'previewDrafts',
    }),
    next: {
      revalidate: isDraftMode ? 0 : 20,
      tags: allTags,
    },
    filterResponse: false
  })
}
